"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ArrowDown } from "lucide-react";
import { createOrderAction, executeOrderAction, cancelOrderAction } from "@/app/actions";
import type { Order } from "@/lib/conduit";
import { fmtAmount } from "@/lib/format";
import SourceSelect from "@/components/source-select";

export type FundingSource = {
  /** Unique option key — a wallet appears once PER ASSET it can hold, so `id` alone is not unique. */
  value: string;
  id: string;
  kind: "virtual_account" | "wallet";
  label: string; // e.g. "USD virtual account" | "USDC · ethereum wallet · 12.00 available"
  /** Which asset this entry moves — required by the order API on wallet sides (a chain holds many assets). */
  assetCode?: string;
  chain?: string;
  /** Current available balance in `assetCode` — drives the "wait for funds" offer (undefined = unknown). */
  available?: number;
};

export default function Converter({ sources }: { sources: FundingSource[] }) {
  const router = useRouter();
  const [pending, start] = useTransition();

  const [sourceValue, setSourceValue] = useState("");
  const [destValue, setDestValue] = useState("");
  const [amount, setAmount] = useState("");
  const [lockSide, setLockSide] = useState<"source" | "destination">("source");
  const [waitForFunds, setWaitForFunds] = useState(false);
  const [quote, setQuote] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Order | null>(null);

  const src = sources.find((s) => s.value === sourceValue);
  const dst = sources.find((s) => s.value === destValue);

  // Only offer "wait for funds" when the source actually can't cover the
  // amount right now — with enough balance the order just executes instantly.
  // The typed amount may be denominated on either side, but every pair here
  // is USD↔stablecoin at ~1:1, so comparing it against the source balance is
  // a sound sufficiency check (fees have been zero on all live orders).
  const insufficient = !!src && !!amount && src.available !== undefined && Number(amount) > src.available;

  // Bidirectional amounts, same as the payout widget: one shared amount +
  // lockSide. Typing in a box locks that side; the other side shows "auto"
  // until the quote fixes it (no rate-preview endpoint to live-sync both).
  const sendValue = lockSide === "source" ? amount : "";
  const receiveValue = lockSide === "destination" ? amount : "";
  function setSendAmount(v: string) {
    setLockSide("source");
    setAmount(v);
  }
  function setReceiveAmount(v: string) {
    setLockSide("destination");
    setAmount(v);
  }

  function getQuote() {
    if (!src || !dst || !amount) return;
    setError(null);
    setDone(null);
    start(async () => {
      const res = await createOrderAction({
        // Wallet sides must say WHICH asset moves — the wallet id alone is
        // ambiguous (one ethereum wallet can hold USDC and USDT side by side).
        source:
          src.kind === "wallet"
            ? { type: "wallet", id: src.id, asset: { code: src.assetCode, chain: src.chain } }
            : { type: "virtual_account", id: src.id },
        destination:
          dst.kind === "wallet"
            ? { type: "wallet", id: dst.id, asset: { code: dst.assetCode, chain: dst.chain } }
            : { type: "virtual_account", id: dst.id },
        lockSide,
        amount,
        // Unfunded order: Conduit's sweep executes it the moment the source
        // balance covers totalDebit — no manual execute call.
        ...(waitForFunds && insufficient ? { autoExecute: true } : {}),
      });
      if (res.ok) setQuote(res.data as Order);
      else {
        const details = (res.fieldErrors ?? []).map((f) => f.detail).filter(Boolean).join("; ");
        setError(details ? `${res.error} — ${details}` : res.error);
      }
    });
  }

  function execute() {
    if (!quote) return;
    setError(null);
    start(async () => {
      const res = await executeOrderAction(quote.id);
      if (res.ok) {
        setDone(res.data as Order);
        setQuote(null);
        router.refresh();
      } else setError(res.error);
    });
  }

  function discard() {
    if (!quote) return;
    const id = quote.id;
    setQuote(null);
    start(async () => {
      await cancelOrderAction(id);
      router.refresh();
    });
  }

  // Same icon-badged dropdown as the Payouts tab's "you send" picker.
  const selector = (value: string, onChange: (v: string) => void, exclude?: string) => (
    <SourceSelect
      items={sources
        .filter((s) => s.value !== exclude)
        .map((s) => ({ value: s.value, code: s.assetCode, label: s.label }))}
      value={value}
      onChange={onChange}
    />
  );

  return (
    <div className="max-w-xl">
      <div className="card rise p-8">
        <div className="card-cream p-5">
          <label className="label">You convert</label>
          {selector(sourceValue, setSourceValue, destValue)}
          <input
            className="field mt-3 text-[22px]"
            style={{ fontWeight: 400 }}
            inputMode="decimal"
            placeholder={lockSide === "source" ? "0.00" : "auto"}
            value={sendValue}
            onChange={(e) => setSendAmount(e.target.value)}
          />
        </div>

        <div className="my-3 flex justify-center">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full"
            style={{ background: "var(--c-green)" }}
          >
            <ArrowDown size={16} />
          </span>
        </div>

        <div className="card-cream p-5">
          <label className="label">You receive</label>
          {selector(destValue, setDestValue, sourceValue)}
          <input
            className="field mt-3 text-[22px]"
            style={{ fontWeight: 400 }}
            inputMode="decimal"
            placeholder={lockSide === "destination" ? "0.00" : "auto"}
            value={receiveValue}
            onChange={(e) => setReceiveAmount(e.target.value)}
          />
        </div>

        <p className="mt-3 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
          {amount
            ? lockSide === "source"
              ? "You convert exactly this much — the amount you receive is fixed by the quote."
              : "You receive exactly this much — the amount converted is fixed by the quote."
            : "Type into either box — whichever side you fill is the exact amount; the other is set by the quote."}
        </p>

        {!quote && (
          <>
            {insufficient && (
              <label className="mt-4 flex cursor-pointer items-start gap-2 text-[13px] font-light">
                <input
                  type="checkbox"
                  className="mt-[3px]"
                  checked={waitForFunds}
                  onChange={(e) => setWaitForFunds(e.target.checked)}
                />
                <span>
                  {src?.assetCode} balance is short ({fmtAmount(src?.available, src?.assetCode)} available) — execute
                  automatically when the funds arrive.
                  <span style={{ color: "var(--c-ink-soft)" }}>
                    {" "}
                    The rate locks now; Conduit runs the conversion the moment the source balance covers it.
                  </span>
                </span>
              </label>
            )}
            <button
              className="btn btn-primary mt-4 w-full"
              disabled={pending || !src || !dst || !amount}
              onClick={getQuote}
            >
              {pending ? "Getting quote…" : waitForFunds && insufficient ? "Lock rate — convert when funded" : "Get quote"}
            </button>
          </>
        )}
      </div>

      {quote && (
        <div className="card-dark rise mt-4 p-8">
          <div className="eyebrow mb-4" style={{ color: "var(--c-on-dark-soft)" }}>
            {quote.autoExecute ? "order armed · rate" : "quote"} · locked until{" "}
            {quote.lockExpiresAt ? new Date(quote.lockExpiresAt).toLocaleTimeString() : "executed"}
          </div>
          <div className="display text-[30px]">
            {fmtAmount((quote.sourceAsset ?? quote.source?.asset)?.amount)} {(quote.sourceAsset ?? quote.source?.asset)?.code} →{" "}
            {fmtAmount((quote.destinationAsset ?? quote.destination?.asset)?.amount)}{" "}
            {(quote.destinationAsset ?? quote.destination?.asset)?.code}
          </div>
          <div className="mt-3 flex flex-col gap-1 text-[14px] font-light" style={{ color: "var(--c-on-dark-soft)" }}>
            {quote.rate?.endUserRate && <span>rate {quote.rate.endUserRate}</span>}
            {quote.fees?.map((f, i) => (
              <span key={i}>
                {f.name.toLowerCase()} {fmtAmount(f.amount)} {f.currency}
              </span>
            ))}
            {quote.totalDebit && (
              <span>
                total debit {fmtAmount(quote.totalDebit)} {(quote.sourceAsset ?? quote.source?.asset)?.code}
              </span>
            )}
          </div>
          {quote.autoExecute && (
            <p className="mt-4 text-[13px] font-light" style={{ color: "var(--c-on-dark-soft)" }}>
              Nothing to click — Conduit checks the source balance every ~30 seconds and executes this order the moment
              it covers the total debit. Fund it before the lock expires or the order cancels as expired.
            </p>
          )}
          <div className="mt-6 flex gap-3">
            {!quote.autoExecute && (
              <button className="btn btn-primary" disabled={pending} onClick={execute}>
                {pending ? "Executing…" : "Execute conversion"}
              </button>
            )}
            <button
              className="btn"
              style={{ background: "transparent", color: "var(--c-white)", boxShadow: "inset 0 0 0 1px var(--c-on-dark-hairline)" }}
              disabled={pending}
              onClick={discard}
            >
              {quote.autoExecute ? "Cancel order" : "Discard"}
            </button>
          </div>
        </div>
      )}

      {done && (
        <div className="card-green rise mt-4 p-8">
          <div className="display text-[24px]">Conversion executed</div>
          <p className="mt-1 text-[14px] font-light">
            Order {done.id} is settling. Track it in Transactions.
          </p>
        </div>
      )}

      {error && (
        <div className="card-cream mt-4 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
